import { CommonActions } from '@react-navigation/native';

import { Study } from '../../../models/Study';
import { StudyParticipant } from '../../../models/StudyParticipant';
import { User } from '../../../models/User';
import { SubscribeTransitionEmitFunc } from '../../../dynamic-forms/DynamicFormContext';

export interface ParticipantStatusChangeConfig {
  targetStatus?: 'ScreenFailed' | 'Enrolled';
}

export interface WithParticipantStatusChangeConfig {
  participantStatusChange?: ParticipantStatusChangeConfig;
}

const DEFAULT_BODY =
  '<p>Hi [First Name],</p><p>Thank you for your interest in [Study Name]. ' +
  "After reviewing your responses, we've determined that you don't meet the " +
  'eligibility criteria for this study at this time. We appreciate you taking ' +
  'the time to participate and hope to have the opportunity to work with you ' +
  'in the future.</p>';

function escapeHtml(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function getMessage(
  study?: Study,
  user?: Pick<User, 'firstName' | 'lastName'>
) {
  let body = study?.config?.screenFailedBody || DEFAULT_BODY;
  const tokens: Record<string, string> = {
    '[First Name]': escapeHtml((user?.firstName || '').replace(/[\r\n]/g, ' ')),
    '[Last Name]': escapeHtml((user?.lastName || '').replace(/[\r\n]/g, ' ')),
    '[Study Name]': escapeHtml((study?.name || '').replace(/[\r\n]/g, ' '))
  };
  for (const [token, value] of Object.entries(tokens)) {
    body = body.split(token).join(value);
  }
  return body;
}

export function isScreenFailedEnabled(
  config?: WithParticipantStatusChangeConfig
) {
  return config?.participantStatusChange?.targetStatus === 'ScreenFailed';
}

export function isEnrolledEnabled(config?: WithParticipantStatusChangeConfig) {
  return config?.participantStatusChange?.targetStatus === 'Enrolled';
}

function getRootNavigation(navigation: any) {
  let root = navigation;
  while (root.getParent?.()) {
    root = root.getParent();
  }
  return root;
}

function resetStudiesStack(
  navigation: any,
  screen: 'ScreenFailedMessage' | 'MyStudies',
  params?: { message: string }
) {
  getRootNavigation(navigation).dispatch(
    CommonActions.reset({
      index: 0,
      routes: [
        {
          name: 'DrawerNavigator',
          state: {
            index: 0,
            routes: [
              {
                name: 'BottomTabNav',
                state: {
                  index: 0,
                  routes: [
                    {
                      name: 'StudiesNav',
                      state: {
                        index: 0,
                        routes: [
                          params ? { name: screen, params } : { name: screen }
                        ]
                      }
                    }
                  ]
                }
              }
            ]
          }
        }
      ]
    })
  );
}

export function showScreenFailed(navigation: any, message: string) {
  resetStudiesStack(navigation, 'ScreenFailedMessage', { message });
}

export function showMyStudies(navigation: any) {
  resetStudiesStack(navigation, 'MyStudies');
}

export async function finishWithScreenFailed(options: {
  navigation: any;
  user?: Pick<User, 'firstName' | 'lastName'> | null;
  studyParticipants?: StudyParticipant[];
  studyParticipantId?: number;
  statePath?: string;
  completeEvent?: (statePath: string) => Promise<void>;
  emitEvent?: SubscribeTransitionEmitFunc;
  updateUserSession?: () => Promise<void>;
}) {
  if (options.completeEvent && options.statePath) {
    await options.completeEvent(options.statePath);
  } else if (options.emitEvent) {
    await options.emitEvent('final', {
      transitions: [],
      statePath: options.statePath
    });
    await options.updateUserSession?.();
  }

  const participant = options.studyParticipants?.find(
    p => p.id === options.studyParticipantId
  );
  showScreenFailed(
    options.navigation,
    getMessage(participant?.study, options.user ?? undefined)
  );
}

export async function finishWithEnrolled(options: {
  navigation: any;
  statePath?: string;
  completeEvent?: (statePath: string) => Promise<void>;
  emitEvent?: SubscribeTransitionEmitFunc;
  updateUserSession?: () => Promise<void>;
}) {
  // Completing the form fires the server enroll (applyParticipantStatusChangeIfConfigured).
  if (options.completeEvent && options.statePath) {
    await options.completeEvent(options.statePath);
  } else if (options.emitEvent) {
    await options.emitEvent('final', {
      transitions: [],
      statePath: options.statePath
    });
    await options.updateUserSession?.();
  }
  showMyStudies(options.navigation);
}
